import { useEffect, useState } from 'react';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { EmailSignupFormContainer } from '#/containers/EmailSignupFormContainer';
import { LoginFormContainer } from '#/containers/LoginFormContainer';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import type { CmsApiSignUpBlockData } from '#/modules/cms/api/types/blocks/CmsApiSignUpBlock';
import { gutterWidth, maxWidth } from '#/theme/theme';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import { CmsSignUpBlockHolder } from './CmsSignUpBlock.styled';
import { CmsSignUpBlockHeadline } from './CmsSignUpBlockHeadline';

type Props = {
  data: CmsApiSignUpBlockData;
  isPageHeader: boolean;
};

export function CmsSignUpBlock({
  data: {
    headline,
    redirectUrl,
    bgImage,
    loggedInHeadline,
    loggedInRedirectButtonText,
  },
  isPageHeader,
}: Props) {
  const { i18n } = useI18n();
  const tmpStorage = redirectUrl
    ? ({ type: 'redirect', redirectUrl } satisfies AuthApiTmpStorage)
    : undefined;

  const { user } = useUser();

  const [emailInUse, setEmailInUse] = useState('');

  useEffect(() => {
    trackEvent('View Sign Up Form');
  }, []);

  const showLoggedInUi = user && loggedInHeadline && !emailInUse;

  return (
    <div data-qa-id="sign-up">
      <CmsSignUpBlockHolder
        $imageHandle={bgImage?.handle}
        $mimetype={bgImage?.mimetype}
      >
        <Box
          mx="auto"
          pt={['58px', null, gutterWidth / 2, '52px']}
          pb={['58px', null, gutterWidth / 2, '96px']}
          px={['24px', null, gutterWidth / 2, gutterWidth]}
          maxWidth={maxWidth[3]}
          width={1}
        >
          <Box display="flex" justifyContent="flex-end">
            <Box
              width={[1, null, 0.5]}
              py={[gutterWidth / 2, null, null, '32px']}
              px={[gutterWidth / 2, null, null, gutterWidth]}
              borderRadius={[8, 0, 12]}
              bg="white"
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
            >
              <CmsSignUpBlockHeadline
                text={showLoggedInUi ? loggedInHeadline : headline}
                isPageHeader={isPageHeader}
              />

              <Box mt="40px">
                {showLoggedInUi && (
                  <>
                    {i18n(
                      'You are logged in as {{strong}}%(email)s{{/strong}}',
                      { email: user.email ?? '', strong: <strong /> },
                    )}

                    {redirectUrl && loggedInRedirectButtonText && (
                      <Box mt={24}>
                        <Button
                          type="link"
                          href={redirectUrl}
                          variant="primary"
                        >
                          {loggedInRedirectButtonText}
                        </Button>
                      </Box>
                    )}
                  </>
                )}

                {!showLoggedInUi && (
                  <>
                    {!emailInUse && (
                      <EmailSignupFormContainer
                        analyticsTitle="[cms] Sign Up block - email available form"
                        usageContext="page"
                        tmpStorage={tmpStorage}
                        compactView
                        hideLoginBlock
                        customButtonText={i18n('Continue')}
                        setEmailInUse={setEmailInUse}
                      />
                    )}

                    {emailInUse && (
                      <LoginFormContainer
                        analyticsTitle="[cms] Sign Up block - email in use"
                        usageContext="page"
                        tmpStorage={tmpStorage}
                        email={emailInUse}
                        compactView
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </CmsSignUpBlockHolder>
    </div>
  );
}
